import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { useUnassignPolicyFromGroupsMutation } from "@dashboard/policies/api";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Group } from "@devices/types";
import { SecondaryButton } from "@shared/CustomButton";
import {
    MinimalTable,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import CustomTablePagination from "@shared/table/TablePagination";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";
import { ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";

import { AssignGroupsModal } from "../AssignGroupsModal/AssignGroupsModal";

import { AssignedGroupRow } from "./AssignedGroupRow";

type Props = {
    policy: API.PolicyViewModel | null;
};

const columns: ColumnDef<Group>[] = [{ id: "name" }, { id: "devices" }, { id: "actions" }];

export const AssignedGroupsSection = ({ policy }: Props) => {
    const { t } = useTranslation(["pages", "common"]);

    const [isAssignModalOpen, setIsAssignModalOpen] = React.useState(false);
    const [unassigningGroupId, setUnassigningGroupId] = React.useState<number | null>(null);

    const allGroups = useSelector(selectGroups);
    const { data: workspace = null } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();

    const allGroupsCannotAssignPolicy = !allGroups.some((group) => groupAbility(group, workspace).can("assign", "Policy"));
    const workspaceCannotAssignPolicy = groupAbility(null, workspace).cannot("assign", "Policy");
    const cannotAssignPolicy = workspaceCannotAssignPolicy && allGroupsCannotAssignPolicy;

    const assignedGroups = React.useMemo(
        () => (policy ? allGroups.filter((group) => policy.groupIds?.includes(group.id) ?? false) : []),
        [allGroups, policy],
    );

    const table = useReactTable<Group>({
        data: assignedGroups,
        columns,
        initialState: { pagination: { pageSize: 12, pageIndex: 0 } },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    const [unassignPolicyFromGroups] = useUnassignPolicyFromGroupsMutation();

    const handleUnassign = async (groupId: number) => {
        if (!policy) {
            return;
        }
        setUnassigningGroupId(groupId);
        try {
            const result = await unassignPolicyFromGroups({ id: policy.id, groupIds: [groupId] });
            if ("error" in result) {
                showErrorToast(getErrorMessage(result.error));
            } else {
                showSuccessToast(t("pages:policiesPage.toast.groupUnassigned"));
            }
        } finally {
            setUnassigningGroupId(null);
        }
    };

    return (
        <CollapsibleProvider initialValue disabled={!policy}>
            <CollapsibleSectionSummary
                action={
                    <SecondaryButton
                        startIcon={<i className="fa-solid fa-plus" />}
                        onClick={() => setIsAssignModalOpen(true)}
                        disabled={!policy || cannotAssignPolicy}
                        tooltipProps={{
                            title: cannotAssignPolicy
                                ? t("common:tooltip.cannotManagePolicies")
                                : t("pages:policiesPage.tooltip.noPolicySelected"),
                            hide: !!policy && !cannotAssignPolicy,
                        }}
                    >
                        {t("pages:policiesPage.button.assignGroup")}
                    </SecondaryButton>
                }
            >
                <CollapsibleSectionTitle>{t("pages:policiesPage.section.assignedGroups.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <MinimalTableContainer>
                    <MinimalTable stickyHeader>
                        <MinimalTableHeader>
                            <MinimalTableRow>
                                <MinimalTableHeaderCell align="left">{t("common:label.name")}</MinimalTableHeaderCell>
                                <MinimalTableHeaderCell align="left">{t("common:label.devices")}</MinimalTableHeaderCell>
                                <MinimalTableHeaderCell align="left">{t("common:label.compliance")}</MinimalTableHeaderCell>
                                <MinimalTableHeaderCell align="right" />
                            </MinimalTableRow>
                        </MinimalTableHeader>
                        <TableBody table={table} noDataMessage={t("pages:policiesPage.section.assignedGroups.empty")}>
                            {table.getRowModel().rows.map((row) => {
                                const group = row.original;
                                return (
                                    <AssignedGroupRow
                                        key={group.id}
                                        group={group}
                                        policyHash={policy?.hash ?? null}
                                        isUnassigning={unassigningGroupId === group.id}
                                        disabled={unassigningGroupId !== null}
                                        onUnassign={handleUnassign}
                                    />
                                );
                            })}
                        </TableBody>
                    </MinimalTable>
                </MinimalTableContainer>
                <CustomTablePagination
                    component="div"
                    rowsPerPageOptions={[12, 24, 48]}
                    count={assignedGroups.length}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        table.setPageSize(size);
                    }}
                />
            </CollapsibleSectionDetails>
            {policy ? (
                <AssignGroupsModal open={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} policy={policy} />
            ) : null}
        </CollapsibleProvider>
    );
};
